<template>
  <div>
    <!-- Loading skeleton -->
    <div v-if="isLoading" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      <div v-for="i in 12" :key="i" class="h-32 rounded-lg bg-gray-100 animate-pulse" />
    </div>

    <!-- Empty state -->
    <div v-else-if="!items.length" class="text-center text-gray-500 py-16">
      <p>{{ mode === 'library' ? $t('IMAGE_LIBRARY.EMPTY.FOLDERS') : $t('IMAGE_LIBRARY.EMPTY.ITEMS') }}</p>
    </div>

    <!-- Grid 12 card/trang -->
    <div v-else class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      <div
        v-for="item in items"
        :key="item.id"
        class="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors cursor-pointer"
        @click="$emit('card-click', item)"
      >
        <div v-if="mode === 'library'" class="flex items-start justify-between">
          <div class="flex items-start gap-3">
            <fluent-icon icon="folder" size="22" class="text-purple-500" />
            <div class="flex-1">
              <div class="font-medium text-gray-900 truncate">{{ item.name }}</div>
              <div class="text-xs text-gray-500 mt-1">{{ item.image_items_count || 0 }} {{ $t('IMAGE_LIBRARY.IMAGES') }}</div>
              <!-- Category display -->
              <div v-if="item.business_category" class="mt-2">
                <span 
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                  :style="{ 
                    backgroundColor: item.business_category.color + '20', 
                    color: item.business_category.color,
                    border: `1px solid ${item.business_category.color}40`
                  }"
                >
                  <span 
                    class="w-2 h-2 rounded-full mr-1"
                    :style="{ backgroundColor: item.business_category.color }"
                  ></span>
                  {{ item.business_category.name }}
                </span>
              </div>
              <div v-else-if="item.category" class="mt-2">
                <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  {{ item.category }}
                </span>
              </div>
            </div>
          </div>
          <woot-dropdown-menu @click.stop>
            <woot-dropdown-item @click="$emit('action', { type: 'edit', item })"><fluent-icon icon="edit" size="16" /></woot-dropdown-item>
            <woot-dropdown-item @click="$emit('action', { type: 'toggle', item })"><fluent-icon icon="eye-show" size="16" /></woot-dropdown-item>
            <woot-dropdown-item variant="danger" @click="$emit('action', { type: 'delete', item })"><fluent-icon icon="delete" size="16" /></woot-dropdown-item>
          </woot-dropdown-menu>
        </div>

        <div v-else>
          <div class="aspect-square w-full overflow-hidden rounded-md bg-gray-50 mb-3">
            <img :src="item.thumb_url || item.file_url || placeholder" class="w-full h-full object-cover" />
          </div>
          <div class="font-medium text-gray-900 truncate">{{ item.title || item.filename }}</div>
          <div v-if="item.description" class="text-xs text-gray-600 mt-1 line-clamp-2">{{ item.description }}</div>
          <div class="text-xs text-gray-500 mt-1">{{ humanSize(item.file_size) }}</div>
          <div class="mt-2 flex justify-end">
            <div class="flex items-center gap-2" @click.stop>
              <woot-button size="small" variant="clear" @click="$emit('action', { type: 'edit', item })">
                <fluent-icon icon="edit" size="16" />
              </woot-button>
              <woot-button size="small" variant="clear" :title="item.is_active ? $t('GENERAL.HIDE') : $t('GENERAL.SHOW')" @click="$emit('action', { type: 'toggle', item })">
                <fluent-icon :icon="item.is_active ? 'eye-hide' : 'eye-show'" size="16" />
              </woot-button>
              <woot-button size="small" variant="clear" @click="$emit('action', { type: 'copy_url', item })">
                <fluent-icon icon="link" size="16" />
              </woot-button>
              <woot-button size="small" variant="clear" @click="$emit('action', { type: 'delete', item })">
                <fluent-icon icon="delete" size="16" class="text-red-600 hover:text-red-700" />
              </woot-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="total > pageSize" class="mt-6 flex justify-end">
      <woot-pagination :current-page="page" :total-items="total" :per-page="pageSize" @page-change="$emit('page-change', $event)" />
    </div>
  </div>
</template>

<script>
import placeholder from 'dashboard/assets/images/no_page_image.png'

export default {
  name: 'ImageGrid',
  props: {
    mode: { type: String, required: true },
    items: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    page: { type: Number, default: 1 },
    total: { type: Number, default: 0 },
    pageSize: { type: Number, default: 12 },
  },
  emits: ['card-click', 'action', 'page-change'],
  data() {
    return { placeholder }
  },
  methods: {
    humanSize(bytes) {
      if (!bytes) return ''
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      const sizes = ['B', 'KB', 'MB', 'GB']
      return `${(bytes / (1024 ** i)).toFixed(2)} ${sizes[i]}`
    },
  },
}
</script>


